/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/queuesApi.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service layer for Queues.
    Centralizes all API calls for better maintainability.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListQueuesParams,
  ListQueuesResponse,
  QueueResponse,
  QuickListItem
} from '../types';

/**
 * API endpoint
 */
export const QUEUES_ENDPOINT = '/api/telephony/queues';

/**
 * Normalize headers for fetch
 */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
}

/**
 * Request API with error handling
 */
async function requestApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ success: true; data: T; message?: string; pagination?: any }> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normalizeHeaders(options.headers),
        ...(options.body && { 'Content-Type': 'application/json' })
      }
    }
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let payload: any;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    return payload;
  }

  return {
    success: true,
    data: undefined as T
  };
}

/**
 * Fetch queues list
 */
export async function fetchQueues(
  params: ListQueuesParams
): Promise<ListQueuesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }

  if (params.domainId != null) {
    queryParams.set('domainId', params.domainId.toString());
  }

  const result = await requestApi<QueueResponse[]>(
    `${QUEUES_ENDPOINT}?${queryParams.toString()}`,
    'Failed to fetch queues'
  );

  return {
    success: result.success,
    data: Array.isArray(result.data) ? result.data : [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0
    }
  };
}

/**
 * Fetch single queue by ID
 */
export async function fetchQueueById(
  id: string | number
): Promise<QueueResponse> {
  const result = await requestApi<QueueResponse>(
    `${QUEUES_ENDPOINT}/${id}`,
    'Failed to fetch queue'
  );
  return result.data;
}

/**
 * Create queue
 */
export async function createQueue(
  payload: {
    name: string;
    context?: string;
    extension?: string | null;
    greeting?: string | null;
    language?: string | null;
    strategy?: string;
    musicOnHold?: string;
    maxWaitTime?: number;
    recordCalls?: boolean;
    enabled?: boolean;
    settings?: unknown;
    tiers?: unknown;
    queueProfileId?: number | null;
    callbackProfileId?: number | null;
    vipListIds?: number[];
  }
): Promise<QueueResponse> {
  const result = await requestApi<QueueResponse>(
    QUEUES_ENDPOINT,
    'Failed to create queue',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Update queue
 */
export async function updateQueue(
  id: string | number,
  payload: {
    name?: string;
    context?: string;
    extension?: string | null;
    greeting?: string | null;
    language?: string | null;
    strategy?: string;
    musicOnHold?: string;
    maxWaitTime?: number;
    recordCalls?: boolean;
    enabled?: boolean;
    settings?: unknown;
    tiers?: unknown;
    queueProfileId?: number | null;
    callbackProfileId?: number | null;
    vipListIds?: number[];
  }
): Promise<QueueResponse> {
  const result = await requestApi<QueueResponse>(
    `${QUEUES_ENDPOINT}/${id}`,
    'Failed to update queue',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Delete queue by ID
 */
export async function deleteQueueById(
  id: string | number
): Promise<void> {
  await requestApi<null>(
    `${QUEUES_ENDPOINT}/${id}`,
    'Failed to delete queue',
    {
      method: 'DELETE'
    }
  );
}

/**
 * Fetch queues for quick list
 */
export async function fetchQuickListQueues(
  limit: number = 100
): Promise<Array<{ id: number; name: string; context?: string | null }>> {
  const result = await fetchQueues({
    page: 1,
    limit,
    sortBy: 'name',
    sortOrder: 'asc'
  });
  return result.data.map(item => ({
    id: item.id,
    name: item.name,
    context: item.context || null
  }));
}

